import MoneyField from 'components/MoneyField';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Icon, TooltipHost } from 'office-ui-fabric-react';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import React, { Component, Fragment } from 'react';
import ToggleField from '../../../../components/ToggleField';
import { biddingStepsDefinitions } from '../biddingStepsDefinitions';

@inject('app', 'sale')
@observer
class IncrementalAuctionPriceEdit extends Component {
  @computed get biddingSteps() {
    return (startingPrice) => {
      let key = 0;
      Object.keys(biddingStepsDefinitions).forEach((b) => {
        if (b <= startingPrice) {
          key = b;
        }
        if (b > startingPrice) return;
      });

      return biddingStepsDefinitions[key];
    };
  }

  @computed get currentBidStep() {
    const {
      store: { round },
    } = this.props;
    if (!round.biddingSteps) {
      return undefined;
    }
    return round.biddingSteps.replace('0=0;1=', '');
  }

  @computed get selectedStepValue() {
    return (startPrice, useStepDefaultValue) => {
      const step = this.biddingSteps(startPrice);
      const stepValue = this.currentBidStep;
      const result =
        stepValue &&
        step.values.some((v) => {
          return v.toString() === stepValue;
        })
          ? stepValue
          : useStepDefaultValue
          ? step.default.toString()
          : 'default'; //step.default.toString();

      return result;
    };
  }

  @observable minPriceChanged = false;

  handleFieldChanged = (field) => (value) => {
    const {
      store: { round },
    } = this.props;
    round[field] = value;
  };

  handleMinPriceChange = (value) => {
    const {
      store: { round },
    } = this.props;
    round.minPrice = value;
    this.minPriceChanged = true;
  };

  handleStartPriceChange = (value) => {
    const {
      store: { round },
    } = this.props;
    round['startPrice'] = value;

    const bid = this.selectedStepValue(value, true);
    round['biddingSteps'] = bid === 'default' ? null : `0=0;1=${bid}`;

    if (!this.minPriceChanged) {
      round.minPrice = value && value >= 100 ? value * 0.8 : null;
    }
  };

  handleBidStepChanged = (option) => {
    const {
      store: { round },
    } = this.props;
    round['biddingSteps'] =
      option.key === 'default' ? null : `0=0;1=${option.key}`;
  };

  handleChoiceChanged = (field) => (_, opt) => {
    var {
      store: { round },
    } = this.props;
    round[field] = opt.key;
  };

  handleToggleChanged = (field) => (_, value) => {
    var {
      store: { round },
    } = this.props;
    round[field] = value;
  };

  componentDidMount() {
    const { overrideStartPrice } = this.props;

    if (!overrideStartPrice) return;

    const step = this.biddingSteps(overrideStartPrice);
    const startPrice = overrideStartPrice + step.default;
    this.handleStartPriceChange(startPrice);
  }

  render() {
    const { app, sale, store, intl, t, overrideStartPrice } = this.props;

    const { round } = store;
    const step = this.biddingSteps(overrideStartPrice || round.startPrice);

    return (
      <Fragment>
        <div>
          <MoneyField
            label={t('Starting price')}
            currency={app.currency.code}
            locale={intl.locale}
            value={round.startPrice}
            onChanged={this.handleStartPriceChange}
            tooltip={t('Starting price - tooltip')}
            autoComplete="off"
            errorMessage={
              !store.startPriceError ? undefined : t(store.startPriceError)
            }
            required
          />
        </div>
        <div style={{ marginTop: '0.5em' }}>
          <ToggleField
            label={t('Flexible bidding')}
            tooltip={t('Flexible bidding - tooltip')}
            onChange={this.handleToggleChanged('enableFlexibleBidding')}
            checked={round.enableFlexibleBidding}
            disabled={round.status === 'opened'}
          />
        </div>

        {!!round.enableFlexibleBidding ? (
          <Fragment>
            <div>
              <ToggleField
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'Hide highest bid / bid history for non-bidders'
                      ),
                    }}
                  ></span>
                }
                tooltip={t(
                  'Hide highest bid / bid history for non-bidders - tooltip'
                )}
                onChange={this.handleToggleChanged(
                  'hideBiddingInfoForNonBidders'
                )}
                checked={round.hideBiddingInfoForNonBidders}
              />
            </div>
            <div>
              <ToggleField
                label={t(
                  sale.saleConfig.roundAllowShowBidsBelowStartingPrice
                    ? 'Allow bidding below starting price'
                    : 'Allow bidding below starting price hidden'
                )}
                tooltip={t('Allow bidding below starting price - tooltip')}
                onChange={this.handleToggleChanged('biddingBelowStartingPrice')}
                checked={round.biddingBelowStartingPrice}
              />
            </div>
          </Fragment>
        ) : null}

        {round.enableFlexibleBidding &&
        sale.saleConfig.roundAllowShowBidsBelowStartingPrice &&
        !!round.biddingBelowStartingPrice ? (
          <div>
            <ToggleField
              label={t('Show bids below starting price')}
              tooltip={t('Show bids below starting price - tooltip')}
              onChange={this.handleToggleChanged('showBidsBelowStartingPrice')}
              checked={round.showBidsBelowStartingPrice}
            />
          </div>
        ) : null}

        {!!round.biddingBelowStartingPrice ? (
          <div>
            <MoneyField
              label={t('Min price')}
              currency={app.currency.code}
              locale={intl.locale}
              value={round.minPrice}
              onChanged={this.handleMinPriceChange}
              tooltip={t('Min price - tooltip')}
              autoComplete="off"
              errorMessage={
                !store.minPriceError ? undefined : t(store.minPriceError)
              }
            />
          </div>
        ) : null}

        {sale.saleConfig.roundShowRedButtonPrice && (
          <div>
            <MoneyField
              label={t('Red button price')}
              currency={app.currency.code}
              locale={intl.locale}
              value={round.redButtonPrice}
              onChanged={this.handleFieldChanged('redButtonPrice')}
              tooltip={t('Red button price - tooltip')}
              autoComplete="off"
            />
          </div>
        )}

        {sale.saleConfig.roundShowBidSteps && (
          <div>
            <Dropdown
              onChanged={this.handleBidStepChanged}
              label={
                <Fragment>
                  {t('Bidding steps')}{' '}
                  <TooltipHost content={t('Bidding steps - tooltip')}>
                    <Icon iconName="Info" />
                  </TooltipHost>
                </Fragment>
              }
              selectedKey={this.selectedStepValue(round.startPrice)}
              options={[
                ...(sale.saleConfig.roundShowDefaultBidSteps
                  ? [{ key: 'default', text: t('Bidding steps - standard') }]
                  : []),
                ...step.values.map((v) => {
                  const str = v.toString();
                  return {
                    key: str,
                    text: v.toFormattedMoney(intl.locale, app.currency.code, 0),
                  };
                }),
              ]}
            />
          </div>
        )}
      </Fragment>
    );
  }
}

export default IncrementalAuctionPriceEdit;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/RoundSection/IncrementalAuction/IncrementalAuctionPriceEdit.jsx